import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { defaultSiteEffectsSettings, ISiteEffectsSettings, SITE_FX_BACKGROUND_MODES } from "../ts/Definitions";
import { getSoundCloudEmbedUrl, isSoundCloudEmbedUrl, isSoundCloudUrl } from "../utilities/audioEmbed";
import { resolveSiteFxMedia } from "../utilities/siteFx";

export type SiteEffectsConfig = ISiteEffectsSettings;
export const defaultSiteEffectsConfig: SiteEffectsConfig = defaultSiteEffectsSettings;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const normalizeCategoryOptions = (categories: string[] | undefined): string[] => {
  if (!categories?.length) return [];
  return Array.from(new Set(categories.filter((category) => !!category).map((c) => c.trim().toLowerCase())))
    .sort((a, b) => a.localeCompare(b));
};

interface ICategoryGroup {
  label: string;
  categories: string[];
}

const hasTypes = (mediaSet: Set<"image" | "video" | "audio">, required: Array<"image" | "video" | "audio">): boolean =>
  required.every((mediaType) => mediaSet.has(mediaType));

const buildCategoryGroups = (
  categories: string[],
  categoryMediaTypes: Record<string, Array<"image" | "video" | "audio">>
): ICategoryGroup[] => {
  const distinctCategories = Array.from(new Set(categories.filter((category) => !!category).map((c) => c.trim().toLowerCase())))
    .sort((a, b) => a.localeCompare(b));

  if (distinctCategories.length === 0) return [];

  const classify = (category: string): Set<"image" | "video" | "audio"> =>
    new Set(categoryMediaTypes[category.trim().toLowerCase()] ?? []);

  const groups: ICategoryGroup[] = [];
  let remaining = [...distinctCategories];
  const addGroup = (label: string, predicate: (mediaSet: Set<"image" | "video" | "audio">) => boolean) => {
    const matches = remaining.filter((category) => predicate(classify(category)));
    if (matches.length > 0) {
      groups.push({ label, categories: matches });
      const matched = new Set(matches);
      remaining = remaining.filter((category) => !matched.has(category));
    }
  };

  addGroup("Image + Video + Audio", (mediaSet) => hasTypes(mediaSet, ["image", "video", "audio"]));
  addGroup("Image + Video", (mediaSet) => hasTypes(mediaSet, ["image", "video"]) && !mediaSet.has("audio"));
  addGroup("Image + Audio", (mediaSet) => hasTypes(mediaSet, ["image", "audio"]) && !mediaSet.has("video"));
  addGroup("All With Image", (mediaSet) => mediaSet.has("image"));
  addGroup("All With Audio", (mediaSet) => mediaSet.has("audio"));
  addGroup("All With Video", (mediaSet) => mediaSet.has("video"));

  if (remaining.length > 0) groups.push({ label: "Available Categories", categories: remaining });

  return groups.length > 0 ? groups : [{ label: "Available Categories", categories: distinctCategories }];
};

const hexToRgba = (hex: string | undefined, alpha: number): string => {
  const fallback = `rgba(0,0,0,${alpha})`;
  if (!hex) return fallback;
  const normalized = hex.replace("#", "").trim();
  if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(normalized)) return fallback;

  const expanded = normalized.length === 3
    ? normalized.split("").map((c) => `${c}${c}`).join("")
    : normalized;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

type VideoSourceKind = "html5" | "youtube";
type BackgroundAudioKind = "html5" | "soundcloud";

const resolveVideoSource = (rawUrl: string): { kind: VideoSourceKind; src: string } => {
  const url = rawUrl.trim();
  if (!url) return { kind: "html5", src: "" };

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    if (host.includes("youtu.be")) {
      const id = parsed.pathname.replace("/", "").split("/")[0];
      if (id) return { kind: "youtube", src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1` };
    }

    if (host.includes("youtube.com")) {
      const watchId = parsed.searchParams.get("v");
      if (watchId) return { kind: "youtube", src: `https://www.youtube.com/embed/${watchId}?autoplay=1&rel=0&modestbranding=1` };

      const parts = parsed.pathname.split("/").filter(Boolean);
      const embedIdx = parts.findIndex((part) => part === "embed" || part === "shorts");
      const id = embedIdx > -1 ? parts[embedIdx + 1] : undefined;
      if (id) return { kind: "youtube", src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1` };
    }
  } catch {
    // Keep original URL for standard HTML5 video playback.
  }

  return { kind: "html5", src: url };
};

interface SiteEffectsFrameworkDialogProps {
  open: boolean;
  armed: boolean;
  config: SiteEffectsConfig;
  onClose: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onConfigChange: (config: SiteEffectsConfig) => void;
  onTestVideo?: () => void;
  showActivationControls?: boolean;
}

const numberInput = (
  label: string,
  value: number,
  min: number,
  max: number,
  onChange: (value: number) => void,
  helperText?: string
) => (
  <TextField
    size="small"
    type="number"
    label={label}
    value={value}
    onChange={(event) => {
      const parsed = Number(event.target.value);
      if (Number.isNaN(parsed)) return;
      onChange(clamp(parsed, min, max));
    }}
    inputProps={{ min, max }}
    helperText={helperText}
    sx={{ minWidth: 150 }}
  />
);

export const SiteEffectsFrameworkDialog: React.FC<SiteEffectsFrameworkDialogProps> = ({
  open,
  armed,
  config,
  onClose,
  onActivate,
  onDeactivate,
  onConfigChange,
  onTestVideo,
  showActivationControls = true,
}) => {
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [categoryMediaTypes, setCategoryMediaTypes] = useState<Record<string, Array<"image" | "video" | "audio">>>({});
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesLoadedOnce, setCategoriesLoadedOnce] = useState(false);
  const [categoriesLoadError, setCategoriesLoadError] = useState<string>("");
  const [selectedCategoryCounts, setSelectedCategoryCounts] = useState<{ image: number; video: number; audio: number }>({
    image: 0,
    video: 0,
    audio: 0,
  });
  const [selectedCategoryCountsLoading, setSelectedCategoryCountsLoading] = useState(false);
  const [selectedCategoryCountsError, setSelectedCategoryCountsError] = useState<string>("");

  const setImages = (patch: Partial<SiteEffectsConfig["images"]>) => {
    onConfigChange({ ...config, images: { ...config.images, ...patch } });
  };

  const setVideos = (patch: Partial<SiteEffectsConfig["videos"]>) => {
    onConfigChange({ ...config, videos: { ...config.videos, ...patch } });
  };

  const setAudio = (patch: Partial<SiteEffectsConfig["audio"]>) => {
    onConfigChange({ ...config, audio: { ...config.audio, ...patch } });
  };

  const setMessages = (patch: Partial<SiteEffectsConfig["messages"]>) => {
    onConfigChange({ ...config, messages: { ...config.messages, ...patch } });
  };

  const imageDurationSec = Math.max(0.05, config.images.imageDurationSec ?? 30);

  const durationMinMs = Math.max(50, config.messages.messageDurationMinMs ?? 200);
  const durationMaxMs = Math.max(durationMinMs, config.messages.messageDurationMaxMs ?? 200);

  const setMessageDurationMin = (value: number) => {
    const nextMin = clamp(value, 50, 15000);
    setMessages({
      messageDurationMinMs: nextMin,
      messageDurationMaxMs: Math.max(nextMin, durationMaxMs),
    });
  };

  const setMessageDurationMax = (value: number) => {
    const nextMax = clamp(value, 50, 20000);
    setMessages({
      messageDurationMinMs: Math.min(durationMinMs, nextMax),
      messageDurationMaxMs: nextMax,
    });
  };

  const [rawMessagesText, setRawMessagesText] = useState(
    () => (config.messages.messages ?? []).join("\n")
  );
  const [rawMiniGameMessagesText, setRawMiniGameMessagesText] = useState(
    () => (config.videos.miniGameButtonMessages ?? []).join("\n")
  );
  const [clipboardStatus, setClipboardStatus] = useState<string>("");
  const prevMessagesRef = useRef(config.messages.messages);
  const prevMiniGameMessagesRef = useRef(config.videos.miniGameButtonMessages);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCategoriesLoading(true);
    setCategoriesLoadError("");
    void resolveSiteFxMedia({
      category: "all",
      mediaTypes: ["image", "video", "audio"],
      bypassCache: true,
    }).then((result) => {
      if (cancelled) return;
      if (!result) {
        setCategoryOptions([]);
        setCategoryMediaTypes({});
        setCategoriesLoadError("Unable to fetch curated categories right now.");
        return;
      }
      setCategoryOptions(normalizeCategoryOptions(result.availableCategories));
      const rawCmt = result.categoryMediaTypes ?? {};
      const normalizedCmt = Object.entries(rawCmt).reduce<Record<string, Array<"image" | "video" | "audio">>>((acc, [k, v]) => {
        const key = (k ?? '').trim().toLowerCase();
        if (key && key !== 'all') acc[key] = v as Array<"image" | "video" | "audio">;
        return acc;
      }, {});
      setCategoryMediaTypes(normalizedCmt);
    }).catch(() => {
      if (cancelled) return;
      setCategoryOptions([]);
      setCategoryMediaTypes({});
      setCategoriesLoadError("Unable to fetch curated categories right now.");
    }).finally(() => {
      if (cancelled) return;
      setCategoriesLoading(false);
      setCategoriesLoadedOnce(true);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selectedCategories = useMemo(
    () => Array.from(new Set((config.siteFxCategories ?? [])
      .map((c) => (c ?? "").trim().toLowerCase())
      .filter((c) => !!c && c !== "all"))),
    [config.siteFxCategories]
  );

  const categoryOptionsWithSelected = useMemo(
    () => Array.from(new Set([...categoryOptions, ...selectedCategories])).sort((a, b) => a.localeCompare(b)),
    [categoryOptions, selectedCategories]
  );

  const groupedCategoryOptions = useMemo(
    () => buildCategoryGroups(categoryOptionsWithSelected, categoryMediaTypes),
    [categoryMediaTypes, categoryOptionsWithSelected]
  );

  useEffect(() => {
    if (!categoriesLoadedOnce || categoriesLoading) return;
    if (categoryOptions.length === 0) return;

    const selected = selectedCategories.filter((c) => categoryOptions.includes(c));
    if (selected.length !== (config.siteFxCategories ?? []).length) {
      onConfigChange({ ...config, siteFxCategories: selected });
    }
  }, [categoriesLoadedOnce, categoriesLoading, categoryOptions, config, onConfigChange, selectedCategories]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const selectedCategories = Array.from(new Set((config.siteFxCategories ?? [])
      .map((c) => (c ?? "").trim().toLowerCase())
      .filter((c) => !!c && c !== "all")));

    const aggregateCounts = (results: Array<{ items: { image: string[]; video: string[]; audio: string[] } } | undefined>) => ({
      image: Array.from(new Set(results.flatMap((r) => r?.items.image ?? []))).length,
      video: Array.from(new Set(results.flatMap((r) => r?.items.video ?? []))).length,
      audio: Array.from(new Set(results.flatMap((r) => r?.items.audio ?? []))).length,
    });

    const hasAnyCounts = (counts: { image: number; video: number; audio: number }) =>
      counts.image > 0 || counts.video > 0 || counts.audio > 0;

    const hasAllMediaTypes = (types: string[] | undefined) => {
      const set = new Set((types ?? []).map((t) => t.toLowerCase()));
      return set.has("image") && set.has("video") && set.has("audio");
    };

    const findFirstAllMediaCategory = (response: { availableCategories?: string[]; categoryMediaTypes?: Record<string, string[]> } | undefined): string | undefined => {
      if (!response) return undefined;
      const available = response.availableCategories ?? [];
      const mediaTypesByCategory = response.categoryMediaTypes ?? {};
      for (const category of available) {
        const exactTypes = mediaTypesByCategory[category];
        const normalizedTypes = mediaTypesByCategory[category.trim().toLowerCase()];
        if (hasAllMediaTypes(exactTypes ?? normalizedTypes)) {
          return category;
        }
      }
      return undefined;
    };

    if (selectedCategories.length === 0) {
      setSelectedCategoryCountsLoading(true);
      setSelectedCategoryCountsError("");
      void resolveSiteFxMedia({
        category: "all",
        mediaTypes: ["image", "video", "audio"],
        bypassCache: true,
      }).then(async (discovery) => {
        if (cancelled) return;
        const fallbackCategory = findFirstAllMediaCategory(discovery as { availableCategories?: string[]; categoryMediaTypes?: Record<string, string[]> } | undefined);
        if (!fallbackCategory) {
          setSelectedCategoryCounts({ image: 0, video: 0, audio: 0 });
          return;
        }
        const fallbackResult = await resolveSiteFxMedia({
          category: fallbackCategory,
          mediaTypes: ["image", "video", "audio"],
          bypassCache: true,
        });
        if (cancelled) return;
        setSelectedCategoryCounts(aggregateCounts([fallbackResult]));
      }).catch(() => {
        if (cancelled) return;
        setSelectedCategoryCounts({ image: 0, video: 0, audio: 0 });
        setSelectedCategoryCountsError("Unable to calculate media counts for selected categories.");
      }).finally(() => {
        if (cancelled) return;
        setSelectedCategoryCountsLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }

    setSelectedCategoryCountsLoading(true);
    setSelectedCategoryCountsError("");
    void Promise.all(
      selectedCategories.map((category) =>
        resolveSiteFxMedia({
          category,
          mediaTypes: ["image", "video", "audio"],
          bypassCache: true,
        })
      )
    ).then((results) => {
      if (cancelled) return;
      const counts = aggregateCounts(results);
      if (hasAnyCounts(counts)) {
        setSelectedCategoryCounts(counts);
        return;
      }

      void resolveSiteFxMedia({
        category: "all",
        mediaTypes: ["image", "video", "audio"],
        bypassCache: true,
      }).then(async (discovery) => {
        if (cancelled) return;
        const fallbackCategory = findFirstAllMediaCategory(discovery as { availableCategories?: string[]; categoryMediaTypes?: Record<string, string[]> } | undefined);
        if (!fallbackCategory) {
          setSelectedCategoryCounts(counts);
          return;
        }
        const fallbackResult = await resolveSiteFxMedia({
          category: fallbackCategory,
          mediaTypes: ["image", "video", "audio"],
          bypassCache: true,
        });
        if (cancelled) return;
        const fallbackCounts = aggregateCounts([fallbackResult]);
        setSelectedCategoryCounts(hasAnyCounts(fallbackCounts) ? fallbackCounts : counts);
      }).catch(() => {
        if (cancelled) return;
        setSelectedCategoryCounts(counts);
      });
    }).catch(() => {
      if (cancelled) return;
      setSelectedCategoryCounts({ image: 0, video: 0, audio: 0 });
      setSelectedCategoryCountsError("Unable to calculate media counts for selected categories.");
    }).finally(() => {
      if (cancelled) return;
      setSelectedCategoryCountsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [config.siteFxCategories, open]);

  const missingEnabledMediaErrors = useMemo(() => {
    const errors: string[] = [];
    if (config.images.enabled && selectedCategoryCounts.image === 0) {
      errors.push("No curated images found for selected categories.");
    }
    if (config.videos.enabled && selectedCategoryCounts.video === 0) {
      errors.push("No curated videos found for selected categories.");
    }
    if (config.audio.enabled && selectedCategoryCounts.audio === 0) {
      errors.push("No curated audio found for selected categories.");
    }
    return errors;
  }, [
    config.audio.enabled,
    config.images.enabled,
    config.videos.enabled,
    selectedCategoryCounts.audio,
    selectedCategoryCounts.image,
    selectedCategoryCounts.video,
  ]);
  useEffect(() => {
    if (prevMessagesRef.current !== config.messages.messages) {
      prevMessagesRef.current = config.messages.messages;
      setRawMessagesText((config.messages.messages ?? []).join("\n"));
    }
  }, [config.messages.messages]);

  useEffect(() => {
    if (prevMiniGameMessagesRef.current !== config.videos.miniGameButtonMessages) {
      prevMiniGameMessagesRef.current = config.videos.miniGameButtonMessages;
      setRawMiniGameMessagesText((config.videos.miniGameButtonMessages ?? []).join("\n"));
    }
  }, [config.videos.miniGameButtonMessages]);

  const normalizeConfig = (input: Partial<SiteEffectsConfig>): SiteEffectsConfig => ({
    backcolour: (input as any)?.backcolour
      ?? (input.messages as any)?.messageBackgroundColor
      ?? defaultSiteEffectsConfig.backcolour,
    siteFxCategories: Array.from(new Set(
      (
        Array.isArray((input as any)?.siteFxCategories)
          ? (input as any).siteFxCategories
          : (input as any)?.siteFxCategory
            ? [(input as any).siteFxCategory]
            : (defaultSiteEffectsConfig.siteFxCategories ?? [])
      )
        .map((c: unknown) => (typeof c === "string" ? c.trim().toLowerCase() : ""))
        .filter((c: string) => !!c && c !== "all")
    )),
    images: {
      ...defaultSiteEffectsConfig.images,
      ...(input.images ?? {}),
      urls: Array.isArray((input.images as any)?.urls)
        ? (input.images as any).urls.filter((v: unknown) => typeof v === "string" && v.trim().length > 0)
        : defaultSiteEffectsConfig.images.urls,
    },
    videos: {
      ...defaultSiteEffectsConfig.videos,
      ...(input.videos ?? {}),
      urls: Array.isArray((input.videos as any)?.urls)
        ? (input.videos as any).urls.filter((v: unknown) => typeof v === "string" && v.trim().length > 0)
        : defaultSiteEffectsConfig.videos.urls,
      miniGameButtonMessages: Array.isArray((input.videos as any)?.miniGameButtonMessages)
        ? (input.videos as any).miniGameButtonMessages.filter((v: unknown) => typeof v === "string" && v.trim().length > 0)
        : defaultSiteEffectsConfig.videos.miniGameButtonMessages,
    },
    audio: {
      ...defaultSiteEffectsConfig.audio,
      ...(input.audio ?? {}),
      urls: Array.isArray((input.audio as any)?.urls)
        ? (input.audio as any).urls.filter((v: unknown) => typeof v === "string" && v.trim().length > 0)
        : defaultSiteEffectsConfig.audio.urls,
    },
    backgroundModes: Array.isArray((input as any)?.backgroundModes)
      ? (input as any).backgroundModes.filter((v: unknown) => typeof v === "string")
      : defaultSiteEffectsConfig.backgroundModes,
    messages: {
      ...defaultSiteEffectsConfig.messages,
      ...(input.messages ?? {}),
      messages: Array.isArray((input.messages as any)?.messages)
        ? (input.messages as any).messages.filter((v: unknown) => typeof v === "string" && v.trim().length > 0)
        : defaultSiteEffectsConfig.messages.messages,
    },
  });

  const handleCopySettings = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(config, null, 2));
      setClipboardStatus("Settings copied to clipboard.");
    } catch {
      setClipboardStatus("Copy failed. Clipboard permission may be blocked.");
    }
  };

  const handlePasteSettings = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text?.trim()) {
        setClipboardStatus("Clipboard was empty.");
        return;
      }
      const parsed = JSON.parse(text) as Partial<SiteEffectsConfig>;
      const normalized = normalizeConfig(parsed);
      onConfigChange(normalized);
      setClipboardStatus("Settings pasted from clipboard.");
    } catch {
      setClipboardStatus("Paste failed. Ensure clipboard contains valid Site FX JSON.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Site FX Framework</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="subtitle2" fontWeight={700}>Runtime Status</Typography>
              {showActivationControls && (
                <Chip
                  size="small"
                  color={armed ? "success" : "default"}
                  label={armed ? "Armed" : "Disarmed"}
                />
              )}
              <TextField
                size="small"
                type="color"
                label="Backcolour"
                value={config.backcolour ?? "#000000"}
                onChange={(event) => onConfigChange({ ...config, backcolour: event.target.value })}
                sx={{ minWidth: 100 }}
                InputLabelProps={{ shrink: true }}
                helperText="Tint colour used by spiral overlays and message backgrounds"
              />
              {showActivationControls && (
                <Typography variant="caption" color="text.secondary">
                  Press ESC at any time to hard-stop all Site FX until you activate again from sidebar.
                </Typography>
              )}
              <Button size="small" variant="outlined" onClick={() => void handleCopySettings()}>Copy Settings</Button>
              <Button size="small" variant="outlined" onClick={() => void handlePasteSettings()}>Paste Settings</Button>
              {clipboardStatus && (
                <Typography variant="caption" color="text.secondary">{clipboardStatus}</Typography>
              )}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Background Modes</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Animated full-screen overlay shown while armed. When multiple modes are enabled they alternate every 60 seconds with a slow crossfade. Backcolour controls the overlay tint.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {SITE_FX_BACKGROUND_MODES.map((mode) => {
                const checked = (config.backgroundModes ?? []).includes(mode.id);
                return (
                  <FormControlLabel
                    key={mode.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...(config.backgroundModes ?? []), mode.id]
                            : (config.backgroundModes ?? []).filter((m) => m !== mode.id);
                          onConfigChange({ ...config, backgroundModes: next });
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{mode.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {mode.id === "arms" ? "Spinning 8-arm conic star pattern" : "Concentric rotating ring spiral"}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Curated Media</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Site FX media is resolved from the selected curated category when the overlay starts. This replaces direct image, video, and audio URL entry in this dialog.
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const values = Array.isArray(selected)
                    ? selected.map((v) => String(v)).filter((v) => !!v)
                    : [];
                  return values.length > 0 ? values.join(", ") : "";
                },
              }}
              label="Media Categories"
              value={selectedCategories}
              onChange={(event) => {
                const rawValue = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
                const value = Array.from(new Set(
                  (rawValue ?? [])
                    .map((v) => (v ?? '').toString().trim().toLowerCase())
                    .filter((v) => !!v && v !== 'all')
                ));
                onConfigChange({ ...config, siteFxCategories: value });
              }}
              helperText="Curated media category used for images, videos, and audio. If selection is empty or no match exists, runtime falls back automatically."
            >
              {categoriesLoading && (
                <MenuItem disabled>Loading categories...</MenuItem>
              )}
              {!categoriesLoading && groupedCategoryOptions.length === 0 && (
                <MenuItem disabled>No categories found from curated sources.</MenuItem>
              )}
              {groupedCategoryOptions.flatMap((group) => [
                <ListSubheader key={`group-${group.label}`} disableSticky>{group.label}</ListSubheader>,
                ...group.categories.map((category) => (
                  <MenuItem key={`${group.label}-${category}`} value={category}>{category}</MenuItem>
                )),
              ])}
            </TextField>
            <Typography variant="caption" color={categoriesLoadError ? "error" : "text.secondary"} sx={{ display: "block", mt: 0.5 }}>
              {categoriesLoadError
                ? categoriesLoadError
                : categoriesLoading
                  ? "Fetching curated category results..."
                  : selectedCategoryCountsLoading
                    ? "Calculating media counts..."
                    : `Resolved media count: ${selectedCategoryCounts.image} Images | ${selectedCategoryCounts.video} Videos | ${selectedCategoryCounts.audio} Audio`}
            </Typography>
            {selectedCategoryCountsError && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                {selectedCategoryCountsError}
              </Typography>
            )}
            {missingEnabledMediaErrors.map((error) => (
              <Typography key={error} variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                {error}
              </Typography>
            ))}
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Images</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Randomly positions curated images over the screen at a set rate. Multiple images can appear at once and auto-close after their duration.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <FormControlLabel control={<Switch checked={config.images.enabled} onChange={(e) => setImages({ enabled: e.target.checked })} />} label="Enable" />
              <FormControlLabel control={<Switch checked={config.images.clickable} onChange={(e) => setImages({ clickable: e.target.checked })} />} label="Clickable" />
              <FormControlLabel control={<Switch checked={config.images.corruption} onChange={(e) => setImages({ corruption: e.target.checked })} />} label="Corruption" />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              <strong>Enable</strong> — turn images on/off. <strong>Clickable</strong> — allow tapping an image to dismiss it early. <strong>Corruption</strong> — apply a glitch distortion effect to images.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {numberInput("Per Min", config.images.perMin, 1, 10, (value) => setImages({ perMin: value }), "Batches of images spawned per minute")}
              {numberInput("Images", config.images.imagesPerFlash, 1, 15, (value) => setImages({ imagesPerFlash: value }), "Images spawned per batch")}
              {numberInput("Max On Screen", config.images.maxOnScreen, 5, 20, (value) => setImages({ maxOnScreen: value }), "Maximum images visible simultaneously")}
              {numberInput("Image Duration (sec)", imageDurationSec, 0.05, 60, (value) => setImages({ imageDurationSec: value }), "Seconds each image stays visible before auto-closing")}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Image URLs are supplied automatically from the selected curated media category.
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Videos</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Plays random videos as full-screen overlays at a set rate. Supports direct video file URLs and YouTube links. Videos are randomly selected from the list, avoiding repeats where possible.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <FormControlLabel control={<Switch checked={config.videos.enabled} onChange={(e) => setVideos({ enabled: e.target.checked })} />} label="Enable" />
              <FormControlLabel control={<Switch checked={config.videos.strictLock} onChange={(e) => setVideos({ strictLock: e.target.checked })} />} label="Strict Lock" />
              {onTestVideo && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onTestVideo}
                >
                  Test Video
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              <strong>Enable</strong> — turn videos on/off. <strong>Strict Lock</strong> — prevent closing the video until it ends naturally (HTML5 files only, ignored for YouTube). <strong>Test Video</strong> — immediately load one random video with current settings.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              {numberInput("Per Hour", config.videos.perHour, 1, 20, (value) => setVideos({ perHour: value }), "How many videos to play per hour on average")}
            </Box>
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>Mini-game</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              A button appears over the video at random intervals. The user must tap it before it disappears. Hitting all targets counts as completing the mini-game.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <FormControlLabel control={<Switch checked={config.videos.miniGameEnabled} onChange={(e) => setVideos({ miniGameEnabled: e.target.checked })} />} label="Enable" />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              <strong>Enable</strong> — show clickable buttons during videos. When disabled videos play without interaction.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              {numberInput("Targets", config.videos.miniGameTargets, 1, 10, (value) => setVideos({ miniGameTargets: value }), "Number of button appearances during the video")}
              {numberInput("Duration (sec)", config.videos.miniGameDurationSec, 10, 30, (value) => setVideos({ miniGameDurationSec: value }), "Seconds the button is visible before it disappears")}
              {numberInput("Size (px)", config.videos.miniGameTargetSizePx, 30, 150, (value) => setVideos({ miniGameTargetSizePx: value }), "Width and height of the clickable button in pixels")}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Video URLs are supplied automatically from the selected curated media category.
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Button messages"
              value={rawMiniGameMessagesText}
              helperText="One message per line — shown on the mini-game button. Randomly selected each time."
              onChange={(event) => setRawMiniGameMessagesText(event.target.value)}
              onBlur={() => {
                const next = rawMiniGameMessagesText
                  .split(/\r?\n/)
                  .map((text) => text.trim())
                  .filter((text) => text.length > 0);
                setVideos({ miniGameButtonMessages: next });
              }}
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Messages</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Displays one text message at a time as a large on-screen overlay. Each message shows for its configured duration then disappears. The same message will not repeat back-to-back.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <FormControlLabel control={<Switch checked={config.messages.enabled} onChange={(e) => setMessages({ enabled: e.target.checked })} />} label="Enable" />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              <strong>Enable</strong> — turn message overlays on/off.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {numberInput("Per Min", config.messages.perMin, 1, 30, (value) => setMessages({ perMin: value }), "How many messages to show per minute")}
              {numberInput("Opacity %", config.messages.opacityPercent, 10, 100, (value) => setMessages({ opacityPercent: value }), "Background panel opacity behind the message text (10–100%)")}
              {numberInput("Duration Min (ms)", durationMinMs, 50, 15000, setMessageDurationMin, "Minimum time in ms a message stays on screen")}
              {numberInput("Duration Max (ms)", durationMaxMs, 50, 20000, setMessageDurationMax, "Maximum time in ms a message stays on screen — actual duration is random within min/max")}
            </Box>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 1 }}
              label="Messages"
              value={rawMessagesText}
              helperText="One message per line — randomly selected, no consecutive repeats"
              onChange={(event) => setRawMessagesText(event.target.value)}
              onBlur={() => setMessages({ messages: rawMessagesText.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0) })}
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Audio</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Plays curated audio as background media at a set rate. The first track plays immediately when armed. Audio runs silently in the background — no visible player UI.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <FormControlLabel control={<Switch checked={config.audio.enabled} onChange={(e) => setAudio({ enabled: e.target.checked })} />} label="Enable" />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              <strong>Enable</strong> — turn background audio on/off.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {numberInput("Per Hour", config.audio.perHour, 1, 10, (value) => setAudio({ perHour: value }), "How many audio tracks to play per hour on average — first plays immediately on arm")}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Audio URLs are supplied automatically from the selected curated media category.
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
        {showActivationControls && (armed ? (
          <Button onClick={onDeactivate} color="warning" variant="contained">Disarm</Button>
        ) : (
          <Button onClick={onActivate} color="primary" variant="contained">Activate</Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};

interface MemberSiteEffectsRuntimeProps {
  armed: boolean;
  config: SiteEffectsConfig;
  onEmergencyStop: () => void;
  onMiniGameMissed?: () => void;
  testVideoTrigger?: number;
  allowEscapeDisarm?: boolean;
  resolverBypassCache?: boolean;
  onAudioDiagnostic?: (message: string) => void;
}

interface ActiveOverlayImage {
  id: string;
  url: string;
  topPct: number;
  leftPct: number;
  widthPx: number;
  rotationDeg: number;
}

interface ActiveOverlayVideo {
  id: string;
  kind: VideoSourceKind;
  src: string;
  rawUrl: string;
}

interface ActiveOverlayMessage {
  id: string;
  text: string;
  backgroundOpacity: number;
  sizePx: number;
  pulseMs: number;
}

interface ActiveBackgroundAudio {
  id: string;
  kind: BackgroundAudioKind;
  src: string;
  rawUrl: string;
}

interface SCWidget {
  play(): void;
  pause(): void;
  bind(event: string, callback: () => void): void;
  unbind(event: string): void;
}

export const MemberSiteEffectsRuntime: React.FC<MemberSiteEffectsRuntimeProps> = ({
  armed,
  config,
  onEmergencyStop,
  onMiniGameMissed,
  testVideoTrigger,
  allowEscapeDisarm = true,
  resolverBypassCache = false,
  onAudioDiagnostic,
}) => {
  const [resolvedMediaUrls, setResolvedMediaUrls] = useState<{
    image: string[];
    video: string[];
    audio: string[];
  }>({
    image: config.images.urls ?? [],
    video: config.videos.urls ?? [],
    audio: config.audio.urls ?? [],
  });

  const [activeImages, setActiveImages] = useState<ActiveOverlayImage[]>([]);
  const [activeVideo, setActiveVideo] = useState<ActiveOverlayVideo | null>(null);
  const [testVideoActive, setTestVideoActive] = useState(false);
  const [activeAudio, setActiveAudio] = useState<ActiveBackgroundAudio | null>(null);
  const [activeMessages, setActiveMessages] = useState<ActiveOverlayMessage[]>([]);
  const [videoCloseLocked, setVideoCloseLocked] = useState(false);
  const [manualPlayNeeded, setManualPlayNeeded] = useState(false);
  const [miniGameButtonVisible, setMiniGameButtonVisible] = useState(false);
  const [miniGameSecondsRemaining, setMiniGameSecondsRemaining] = useState(0);
  const [miniGameTargetTopPct, setMiniGameTargetTopPct] = useState(22);
  const [miniGameTargetLeftPct, setMiniGameTargetLeftPct] = useState(74);
  const [miniGameButtonText, setMiniGameButtonText] = useState("Tap now");
  const closeTimersRef = useRef<Map<string, number>>(new Map());
  const messageCloseTimersRef = useRef<Map<string, number>>(new Map());
  const messageSpawnTimersRef = useRef<number[]>([]);
  const messageScheduleTimeoutRef = useRef<number | undefined>(undefined);
  const lastMessageRef = useRef<string | null>(null);
  const imagePool = useRef<string[]>([]);
  const videoPool = useRef<string[]>([]);
  const audioPool = useRef<string[]>([]);
  const messagePool = useRef<string[]>([]);
  const videoLastPlayedAtRef = useRef<Record<string, number>>({});
  const recentVideoQueueRef = useRef<string[]>([]);
  const videoSpawnTimeoutRef = useRef<number | undefined>(undefined);
  const audioSpawnTimeoutRef = useRef<number | undefined>(undefined);
  const activeVideoRef = useRef<ActiveOverlayVideo | null>(null);
  const activeAudioRef = useRef<ActiveBackgroundAudio | null>(null);
  const miniGameShowTimerRef = useRef<number | undefined>(undefined);
  const miniGameHideTimerRef = useRef<number | undefined>(undefined);
  const miniGameCountdownRef = useRef<number | undefined>(undefined);
  const scheduleMiniGameButtonRef = useRef<() => void>(() => { });
  const html5VideoRef = useRef<HTMLVideoElement | null>(null);
  const htmlAudioRef = useRef<HTMLAudioElement | null>(null);
  const soundCloudAudioIframeRef = useRef<HTMLIFrameElement | null>(null);
  const soundCloudWidgetRef = useRef<SCWidget | null>(null);

  const emitAudioDiagnostic = useCallback((message: string) => {
    onAudioDiagnostic?.(`${new Date().toLocaleTimeString()} ${message}`);
  }, [onAudioDiagnostic]);

  useEffect(() => {
    let cancelled = false;

    setResolvedMediaUrls({
      image: config.images.urls ?? [],
      video: config.videos.urls ?? [],
      audio: config.audio.urls ?? [],
    });

    const selectedCategories = Array.from(
      new Set((config.siteFxCategories ?? []).filter((c) => !!c).map((c) => c.trim()).filter((c) => !!c))
    );
    const categoriesToResolve = selectedCategories.includes("all") ? ["all"] : selectedCategories;

    const aggregateItems = (results: Array<{ items: { image: string[]; video: string[]; audio: string[] } } | undefined>) => ({
      image: Array.from(new Set(results.flatMap((r) => r?.items.image ?? []))),
      video: Array.from(new Set(results.flatMap((r) => r?.items.video ?? []))),
      audio: Array.from(new Set(results.flatMap((r) => r?.items.audio ?? []))),
    });
    const hasAnyItems = (items: { image: string[]; video: string[]; audio: string[] }) =>
      items.image.length > 0 || items.video.length > 0 || items.audio.length > 0;

    const hasAllMediaTypes = (types: string[] | undefined) => {
      const set = new Set((types ?? []).map((t) => t.toLowerCase()));
      return set.has("image") && set.has("video") && set.has("audio");
    };

    const findFirstAllMediaCategory = (response: { availableCategories?: string[]; categoryMediaTypes?: Record<string, string[]> } | undefined): string | undefined => {
      if (!response) return undefined;
      const available = response.availableCategories ?? [];
      const mediaTypesByCategory = response.categoryMediaTypes ?? {};
      for (const category of available) {
        const exactTypes = mediaTypesByCategory[category];
        const normalizedTypes = mediaTypesByCategory[category.trim().toLowerCase()];
        if (hasAllMediaTypes(exactTypes ?? normalizedTypes)) {
          return category;
        }
      }
      return undefined;
    };

    void (async () => {
      try {
        let results = await Promise.all(
          (categoriesToResolve.length > 0 ? categoriesToResolve : ["all"]).map((category) =>
            resolveSiteFxMedia({
              category,
              mediaTypes: ["image", "video", "audio"],
              bypassCache: resolverBypassCache,
            })
          )
        );

        let aggregated = aggregateItems(results as Array<{ items: { image: string[]; video: string[]; audio: string[] } } | undefined>);

        if (categoriesToResolve.length === 0 || !hasAnyItems(aggregated)) {
          const discovery = await resolveSiteFxMedia({
            category: "all",
            mediaTypes: ["image", "video", "audio"],
            bypassCache: resolverBypassCache,
          });
          const fallbackCategory = findFirstAllMediaCategory(discovery as { availableCategories?: string[]; categoryMediaTypes?: Record<string, string[]> } | undefined);
          if (fallbackCategory) {
            results = await Promise.all([
              resolveSiteFxMedia({
                category: fallbackCategory,
                mediaTypes: ["image", "video", "audio"],
                bypassCache: resolverBypassCache,
              }),
            ]);
            const fallbackAggregated = aggregateItems(results as Array<{ items: { image: string[]; video: string[]; audio: string[] } } | undefined>);
            if (hasAnyItems(fallbackAggregated)) {
              aggregated = fallbackAggregated;
            }
          }
        }

        if (!cancelled) {
          emitAudioDiagnostic(`Resolver complete: audio=${aggregated.audio.length}, sample=${aggregated.audio[0] ?? "none"}`);
          setResolvedMediaUrls(aggregated);
        }
      } catch {
        // Keep current URLs if curated resolution fails in local runtime/test mode.
        emitAudioDiagnostic("Resolver failed for curated media.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config.audio.urls, config.images.urls, config.siteFxCategories, config.videos.urls, resolverBypassCache]);

  useEffect(() => {
    const configured = resolvedMediaUrls.image ?? [];
    imagePool.current = Array.from(new Set(configured.filter(Boolean)));
  }, [resolvedMediaUrls.image]);

  useEffect(() => {
    const configured = resolvedMediaUrls.video ?? [];
    const normalized = Array.from(new Set(configured.filter(Boolean).map((url) => url.trim()).filter(Boolean)));
    videoPool.current = normalized;

    const allowed = new Set(normalized);
    const prunedTimestamps: Record<string, number> = {};
    Object.entries(videoLastPlayedAtRef.current).forEach(([url, timestamp]) => {
      if (allowed.has(url)) prunedTimestamps[url] = timestamp;
    });
    videoLastPlayedAtRef.current = prunedTimestamps;
    recentVideoQueueRef.current = recentVideoQueueRef.current.filter((url) => allowed.has(url));
  }, [resolvedMediaUrls.video]);

  useEffect(() => {
    const configured = resolvedMediaUrls.audio ?? [];
    audioPool.current = Array.from(new Set(configured.filter(Boolean).map((url) => url.trim()).filter(Boolean)));
    emitAudioDiagnostic(`Audio pool updated: ${audioPool.current.length} item(s).`);
  }, [resolvedMediaUrls.audio]);

  useEffect(() => {
    const configured = config.messages.messages ?? [];
    messagePool.current = Array.from(
      new Set(configured.filter(Boolean).map((message) => message.trim()).filter(Boolean))
    );
  }, [config.messages.messages]);

  useEffect(() => {
    activeVideoRef.current = activeVideo;
  }, [activeVideo]);

  useEffect(() => {
    activeAudioRef.current = activeAudio;
  }, [activeAudio]);

  useEffect(() => {
    const needsSoundCloud = (resolvedMediaUrls.audio ?? []).some((url) => isSoundCloudUrl(url));
    const needsSoundCloudWidget = (resolvedMediaUrls.audio ?? []).some((url) => isSoundCloudEmbedUrl(url));
    if (!needsSoundCloudWidget) return;
    if (!needsSoundCloud || document.getElementById("sc-widget-api")) return;

    const script = document.createElement("script");
    script.id = "sc-widget-api";
    script.src = "https://w.soundcloud.com/player/api.js";
    script.async = true;
    document.body.appendChild(script);
    emitAudioDiagnostic("Loaded SoundCloud widget script for embed URL playback.");
  }, [resolvedMediaUrls.audio]);

  const removeImage = useCallback((id: string) => {
    const timer = closeTimersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      closeTimersRef.current.delete(id);
    }
    setActiveImages((current) => current.filter((img) => img.id !== id));
  }, []);

  const clearAllImages = useCallback(() => {
    closeTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    closeTimersRef.current.clear();
    setActiveImages([]);
  }, []);

  const removeMessage = useCallback((id: string) => {
    const timer = messageCloseTimersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      messageCloseTimersRef.current.delete(id);
    }
    setActiveMessages((current) => current.filter((message) => message.id !== id));
  }, []);

  const clearMessageSpawnTimers = useCallback(() => {
    messageSpawnTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    messageSpawnTimersRef.current = [];
    if (messageScheduleTimeoutRef.current !== undefined) {
      window.clearTimeout(messageScheduleTimeoutRef.current);
      messageScheduleTimeoutRef.current = undefined;
    }
  }, []);

  const clearAllMessages = useCallback(() => {
    clearMessageSpawnTimers();
    messageCloseTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    messageCloseTimersRef.current.clear();
    setActiveMessages([]);
  }, [clearMessageSpawnTimers]);

  const clearMiniGameTimers = useCallback(() => {
    if (miniGameShowTimerRef.current !== undefined) {
      window.clearTimeout(miniGameShowTimerRef.current);
      miniGameShowTimerRef.current = undefined;
    }
    if (miniGameHideTimerRef.current !== undefined) {
      window.clearTimeout(miniGameHideTimerRef.current);
      miniGameHideTimerRef.current = undefined;
    }
    if (miniGameCountdownRef.current !== undefined) {
      window.clearInterval(miniGameCountdownRef.current);
      miniGameCountdownRef.current = undefined;
    }
    setMiniGameButtonVisible(false);
    setMiniGameSecondsRemaining(0);
  }, []);

  const clearVideoSpawnTimeout = useCallback(() => {
    if (videoSpawnTimeoutRef.current !== undefined) {
      window.clearTimeout(videoSpawnTimeoutRef.current);
      videoSpawnTimeoutRef.current = undefined;
    }
  }, []);

  const clearAudioSpawnTimeout = useCallback(() => {
    if (audioSpawnTimeoutRef.current !== undefined) {
      window.clearTimeout(audioSpawnTimeoutRef.current);
      audioSpawnTimeoutRef.current = undefined;
    }
  }, []);

  const resetVideoState = useCallback(() => {
    clearMiniGameTimers();
    setActiveVideo(null);
    setTestVideoActive(false);
    setVideoCloseLocked(false);
    setManualPlayNeeded(false);
    setMiniGameButtonText("Tap now");
    activeVideoRef.current = null;
    if (html5VideoRef.current) {
      html5VideoRef.current.pause();
      html5VideoRef.current.removeAttribute("src");
      html5VideoRef.current.load();
    }
  }, [clearMiniGameTimers]);

  const getSoundCloudWidget = useCallback((): SCWidget | null => {
    const SC = (window as any).SC;
    if (!SC || !soundCloudAudioIframeRef.current) return null;
    if (!soundCloudWidgetRef.current) {
      soundCloudWidgetRef.current = SC.Widget(soundCloudAudioIframeRef.current) as SCWidget;
    }
    return soundCloudWidgetRef.current;
  }, []);

  const resetAudioState = useCallback(() => {
    setActiveAudio(null);
    activeAudioRef.current = null;
    if (htmlAudioRef.current) {
      htmlAudioRef.current.pause();
      htmlAudioRef.current.removeAttribute("src");
      htmlAudioRef.current.load();
    }
    try {
      getSoundCloudWidget()?.pause();
    } catch {
      // Ignore missing SoundCloud widget state.
    }
    soundCloudWidgetRef.current = null;
  }, [getSoundCloudWidget]);

  const pickMiniGameButtonText = useCallback(() => {
    const messages = config.videos.miniGameButtonMessages ?? [];
    if (messages.length === 0) {
      setMiniGameButtonText("Tap now");
      return;
    }
    setMiniGameButtonText(messages[Math.floor(Math.random() * messages.length)]);
  }, [config.videos.miniGameButtonMessages]);

  const randomizeMiniGameTargetPosition = useCallback(() => {
    setMiniGameTargetTopPct(12 + Math.random() * 72);
    setMiniGameTargetLeftPct(8 + Math.random() * 84);
  }, []);

  const scheduleMiniGameButton = useCallback(() => {
    if (miniGameShowTimerRef.current !== undefined) {
      window.clearTimeout(miniGameShowTimerRef.current);
      miniGameShowTimerRef.current = undefined;
    }
    if (miniGameHideTimerRef.current !== undefined) {
      window.clearTimeout(miniGameHideTimerRef.current);
      miniGameHideTimerRef.current = undefined;
    }

    const delayMs = 8000 + Math.random() * 22000;
    miniGameShowTimerRef.current = window.setTimeout(() => {
      if (!activeVideoRef.current) return;
      pickMiniGameButtonText();
      setMiniGameTargetTopPct(12 + Math.random() * 72);
      setMiniGameTargetLeftPct(8 + Math.random() * 84);
      setMiniGameButtonVisible(true);

      const visibleSec = Math.max(3, Math.min(30, config.videos.miniGameDurationSec));
      setMiniGameSecondsRemaining(visibleSec);
      if (miniGameCountdownRef.current !== undefined) {
        window.clearInterval(miniGameCountdownRef.current);
      }
      miniGameCountdownRef.current = window.setInterval(() => {
        setMiniGameSecondsRemaining((s) => Math.max(0, s - 1));
      }, 1000);

      const visibleMs = visibleSec * 1000;
      miniGameHideTimerRef.current = window.setTimeout(() => {
        if (activeVideoRef.current) {
          onMiniGameMissed?.();
        }
        if (miniGameCountdownRef.current !== undefined) {
          window.clearInterval(miniGameCountdownRef.current);
          miniGameCountdownRef.current = undefined;
        }
        setMiniGameButtonVisible(false);
        setMiniGameSecondsRemaining(0);
        scheduleMiniGameButtonRef.current();
      }, visibleMs);
    }, delayMs);
  }, [config.videos.miniGameDurationSec, onMiniGameMissed, pickMiniGameButtonText]);

  useEffect(() => {
    scheduleMiniGameButtonRef.current = scheduleMiniGameButton;
  }, [scheduleMiniGameButton]);

  const pickNextVideoUrl = useCallback((): string | undefined => {
    const pool = videoPool.current;
    if (pool.length === 0) return undefined;

    const now = Date.now();
    const baseIntervalMs = Math.max(1000, Math.floor(3600000 / Math.max(1, config.videos.perHour)));
    const cooldownMs = Math.min(15 * 60 * 1000, Math.max(45 * 1000, Math.floor(baseIntervalMs * 0.35)));
    const recentWindow = Math.max(1, Math.min(3, pool.length - 1));
    const recentSet = new Set(recentVideoQueueRef.current.slice(-recentWindow));

    const notRecent = pool.filter((url) => !recentSet.has(url));
    const cooledNotRecent = notRecent.filter((url) => {
      const last = videoLastPlayedAtRef.current[url] ?? 0;
      return now - last >= cooldownMs;
    });
    const cooledAny = pool.filter((url) => {
      const last = videoLastPlayedAtRef.current[url] ?? 0;
      return now - last >= cooldownMs;
    });

    const candidates =
      cooledNotRecent.length > 0 ? cooledNotRecent
        : notRecent.length > 0 ? notRecent
          : cooledAny.length > 0 ? cooledAny
            : pool;

    return candidates[Math.floor(Math.random() * candidates.length)];
  }, [config.videos.perHour]);

  const spawnVideo = useCallback(() => {
    if (activeVideoRef.current || videoPool.current.length === 0) return;

    const nextUrl = pickNextVideoUrl();
    if (!nextUrl) return;

    const source = resolveVideoSource(nextUrl);
    if (!source.src) return;

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const supportsEndedLock = source.kind === "html5";
    const runMiniGame = config.videos.miniGameEnabled;
    const strictEndedLock = config.videos.strictLock && !runMiniGame && supportsEndedLock;

    clearMiniGameTimers();
    setManualPlayNeeded(false);
    const nextVideo: ActiveOverlayVideo = { id, kind: source.kind, src: source.src, rawUrl: nextUrl };
    setActiveVideo(nextVideo);
    activeVideoRef.current = nextVideo;
    videoLastPlayedAtRef.current[nextUrl] = Date.now();
    recentVideoQueueRef.current = [...recentVideoQueueRef.current, nextUrl].slice(-6);

    if (runMiniGame) {
      setVideoCloseLocked(config.videos.strictLock);
      scheduleMiniGameButton();
      return;
    }

    setVideoCloseLocked(strictEndedLock);
  }, [
    clearMiniGameTimers,
    config.videos.miniGameEnabled,
    pickNextVideoUrl,
    config.videos.strictLock,
    scheduleMiniGameButton,
  ]);

  const spawnAudio = useCallback(() => {
    if (activeAudioRef.current || audioPool.current.length === 0) return;

    const nextUrl = audioPool.current[Math.floor(Math.random() * audioPool.current.length)];
    if (!nextUrl) return;

    const nextAudio: ActiveBackgroundAudio = isSoundCloudEmbedUrl(nextUrl)
      ? {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        kind: "soundcloud",
        src: getSoundCloudEmbedUrl(nextUrl, true),
        rawUrl: nextUrl,
      }
      : {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        kind: "html5",
        src: nextUrl,
        rawUrl: nextUrl,
      };

    emitAudioDiagnostic(`Spawning audio: kind=${nextAudio.kind}, raw=${nextAudio.rawUrl}`);

    setActiveAudio(nextAudio);
    activeAudioRef.current = nextAudio;
  }, [emitAudioDiagnostic]);

  const scheduleNextVideoSpawn = useCallback(() => {
    clearVideoSpawnTimeout();

    if (!armed || !config.videos.enabled || videoPool.current.length === 0) return;

    const baseIntervalMs = Math.max(1000, Math.floor(3600000 / Math.max(1, config.videos.perHour)));
    const delay = baseIntervalMs;

    videoSpawnTimeoutRef.current = window.setTimeout(() => {
      if (!activeVideoRef.current) {
        spawnVideo();
      }
      scheduleNextVideoSpawn();
    }, delay);
  }, [armed, clearVideoSpawnTimeout, config.videos.enabled, config.videos.perHour, spawnVideo]);

  useEffect(() => {
    if (!testVideoTrigger) return;
    setTestVideoActive(true);
    spawnVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testVideoTrigger]);

  useEffect(() => {
    if (!testVideoActive) return;
    if (activeVideoRef.current) return;
    if (videoPool.current.length === 0) return;
    spawnVideo();
  }, [resolvedMediaUrls.video, spawnVideo, testVideoActive]);

  const scheduleNextAudioSpawn = useCallback(() => {
    clearAudioSpawnTimeout();

    if (!armed || !config.audio.enabled || audioPool.current.length === 0) return;

    const baseIntervalMs = Math.max(1000, Math.floor(3600000 / Math.max(1, config.audio.perHour)));
    const minDelay = Math.max(1000, Math.floor(baseIntervalMs * 0.65));
    const maxDelay = Math.max(minDelay + 1, Math.floor(baseIntervalMs * 1.35));
    const delay = minDelay + Math.floor(Math.random() * (maxDelay - minDelay));

    audioSpawnTimeoutRef.current = window.setTimeout(() => {
      if (!activeAudioRef.current) {
        spawnAudio();
      }
      scheduleNextAudioSpawn();
    }, delay);
  }, [armed, clearAudioSpawnTimeout, config.audio.enabled, config.audio.perHour, spawnAudio]);

  const closeActiveVideo = useCallback(() => {
    if (videoCloseLocked) return;
    resetVideoState();
  }, [resetVideoState, videoCloseLocked]);

  const hitMiniGameTarget = useCallback(() => {
    if (miniGameHideTimerRef.current !== undefined) {
      window.clearTimeout(miniGameHideTimerRef.current);
      miniGameHideTimerRef.current = undefined;
    }
    if (miniGameCountdownRef.current !== undefined) {
      window.clearInterval(miniGameCountdownRef.current);
      miniGameCountdownRef.current = undefined;
    }
    setMiniGameButtonVisible(false);
    setMiniGameSecondsRemaining(0);
    scheduleMiniGameButton();
  }, [scheduleMiniGameButton]);

  const spawnImages = useCallback((requestedCount: number) => {
    if (imagePool.current.length === 0) return;

    setActiveImages((current) => {
      const room = Math.max(0, config.images.maxOnScreen - current.length);
      const count = Math.min(room, requestedCount);
      if (count <= 0) return current;

      const created: ActiveOverlayImage[] = [];
      for (let i = 0; i < count; i += 1) {
        const randomUrl = imagePool.current[Math.floor(Math.random() * imagePool.current.length)];
        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        created.push({
          id,
          url: randomUrl,
          leftPct: Math.random() * 88,
          topPct: Math.random() * 82,
          widthPx: 140 + Math.floor(Math.random() * 200),
          rotationDeg: -10 + Math.random() * 20,
        });
      }

      const durationMs = Math.max(50, Math.floor((config.images.imageDurationSec ?? 30) * 1000));
      created.forEach((overlayImage) => {
        const timer = window.setTimeout(() => removeImage(overlayImage.id), durationMs);
        closeTimersRef.current.set(overlayImage.id, timer);
      });

      return [...current, ...created];
    });
  }, [config.images.imageDurationSec, config.images.maxOnScreen, removeImage]);

  const spawnSingleMessage = useCallback(() => {
    const pool = messagePool.current;
    if (pool.length === 0) return;

    let text: string;
    if (pool.length === 1) {
      text = pool[0];
    } else {
      const candidates = pool.filter((m) => m !== lastMessageRef.current);
      text = candidates[Math.floor(Math.random() * candidates.length)];
    }
    lastMessageRef.current = text;

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const textLength = text.length;
    const baseSize = Math.max(52, Math.min(190, 220 - Math.floor(textLength * 1.8)));
    const sizePx = Math.max(48, baseSize + Math.floor(Math.random() * 10) - 5);
    const overlayMessage: ActiveOverlayMessage = {
      id,
      text,
      backgroundOpacity: Math.max(0.08, Math.min(0.65, (config.messages.opacityPercent / 100) * 0.65)),
      sizePx,
      pulseMs: 650 + Math.floor(Math.random() * 450),
    };

    messageCloseTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    messageCloseTimersRef.current.clear();
    setActiveMessages([overlayMessage]);

    const minMs = Math.max(50, Math.min(config.messages.messageDurationMinMs ?? 200, config.messages.messageDurationMaxMs ?? 200));
    const maxMs = Math.max(minMs, Math.max(config.messages.messageDurationMinMs ?? 200, config.messages.messageDurationMaxMs ?? 200));
    const ttlMs = minMs + Math.random() * (maxMs - minMs);
    const timer = window.setTimeout(() => removeMessage(overlayMessage.id), ttlMs);
    messageCloseTimersRef.current.set(overlayMessage.id, timer);
  }, [config.messages.messageDurationMaxMs, config.messages.messageDurationMinMs, config.messages.opacityPercent, removeMessage]);

  const spawnMessageBurst = useCallback((requestedFrames: number) => {
    if (messagePool.current.length === 0) return;

    clearMessageSpawnTimers();
    const burstCount = Math.max(1, Math.min(8, requestedFrames));
    const flashGapMs = 160;

    for (let i = 0; i < burstCount; i += 1) {
      const timer = window.setTimeout(() => {
        spawnSingleMessage();
      }, i * flashGapMs);
      messageSpawnTimersRef.current.push(timer);
    }
  }, [clearMessageSpawnTimers, spawnSingleMessage]);

  useEffect(() => {
    if (!armed || !allowEscapeDisarm) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      onEmergencyStop();
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [allowEscapeDisarm, armed, onEmergencyStop]);

  useEffect(() => {
    if (!armed || !config.images.enabled) {
      clearAllImages();
      return;
    }
    if (imagePool.current.length === 0) {
      clearAllImages();
      return;
    }

    const intervalMs = Math.max(1000, Math.floor(60000 / Math.max(1, config.images.perMin)));
    const id = window.setInterval(() => {
      spawnImages(config.images.imagesPerFlash);
    }, intervalMs);
    return () => {
      window.clearInterval(id);
    };
  }, [armed, clearAllImages, config.images.enabled, config.images.imagesPerFlash, config.images.perMin, spawnImages]);

  useEffect(() => () => clearAllImages(), [clearAllImages]);

  useEffect(() => {
    if (!armed || !config.images.enabled) return;
    if ((resolvedMediaUrls.image ?? []).length === 0) return;
    spawnImages(Math.max(1, config.images.imagesPerFlash));
  }, [armed, config.images.enabled, config.images.imagesPerFlash, resolvedMediaUrls.image, spawnImages]);

  useEffect(() => {
    if (!armed || !config.audio.enabled) {
      emitAudioDiagnostic("Audio disabled or overlay not armed.");
      resetAudioState();
      clearAudioSpawnTimeout();
      return;
    }
    if (audioPool.current.length === 0) {
      emitAudioDiagnostic("Audio pool empty while armed.");
      resetAudioState();
      clearAudioSpawnTimeout();
      return;
    }

    // Play first audio immediately, then schedule subsequent ones
    if (!activeAudioRef.current) {
      emitAudioDiagnostic("Starting first audio immediately.");
      spawnAudio();
    }
    scheduleNextAudioSpawn();
    return () => clearAudioSpawnTimeout();
  }, [armed, clearAudioSpawnTimeout, config.audio.enabled, config.audio.perHour, emitAudioDiagnostic, resetAudioState, resolvedMediaUrls.audio, scheduleNextAudioSpawn, spawnAudio]);

  useEffect(() => {
    if (!armed || !config.videos.enabled) {
      if (!testVideoActive) {
        resetVideoState();
      }
      clearVideoSpawnTimeout();
      return;
    }
    if (videoPool.current.length === 0) {
      if (activeVideoRef.current) {
        resetVideoState();
      }
      clearVideoSpawnTimeout();
      return;
    }

    scheduleNextVideoSpawn();
    return () => clearVideoSpawnTimeout();
  }, [
    armed,
    clearVideoSpawnTimeout,
    config.videos.enabled,
    config.videos.perHour,
    resetVideoState,
    resolvedMediaUrls.video,
    scheduleNextVideoSpawn,
    spawnVideo,
    testVideoActive,
  ]);

  useEffect(() => {
    if (!activeVideo || activeVideo.kind !== "html5") return;
    const video = html5VideoRef.current;
    if (!video) return;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        setManualPlayNeeded(true);
      });
    }
  }, [activeVideo]);

  useEffect(() => {
    if (!activeAudio || activeAudio.kind !== "html5") return;
    const audio = htmlAudioRef.current;
    if (!audio) return;

    emitAudioDiagnostic(`Attempting HTML5 audio play: ${activeAudio.src}`);
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        emitAudioDiagnostic("HTML5 audio play failed (autoplay/policy or invalid source).");
        resetAudioState();
      });
      void playPromise.then(() => {
        emitAudioDiagnostic("HTML5 audio playback started.");
      });
    }
  }, [activeAudio, emitAudioDiagnostic, resetAudioState]);

  useEffect(() => {
    if (!activeAudio || activeAudio.kind !== "soundcloud") return;
    emitAudioDiagnostic("Using SoundCloud embed audio playback path.");

    // auto_play=true in embed URL handles playback; we only need Widget API to detect finish.
    let cancelled = false;
    let retryTimer: number | undefined;

    const bindFinish = () => {
      if (cancelled) return;
      const SC = (window as any).SC;
      const widget = getSoundCloudWidget();
      if (!SC || !widget) {
        retryTimer = window.setTimeout(bindFinish, 500);
        return;
      }
      const finishEvent = SC?.Widget?.Events?.FINISH ?? "finish";
      widget.bind(finishEvent, () => {
        if (!cancelled) resetAudioState();
      });
    };

    retryTimer = window.setTimeout(bindFinish, 500);

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [activeAudio, getSoundCloudWidget, resetAudioState]);

  useEffect(() => () => {
    clearVideoSpawnTimeout();
    resetVideoState();
  }, [clearVideoSpawnTimeout, resetVideoState]);

  useEffect(() => () => {
    clearAudioSpawnTimeout();
    resetAudioState();
  }, [clearAudioSpawnTimeout, resetAudioState]);

  useEffect(() => {
    if (!armed || !config.messages.enabled) {
      clearAllMessages();
      return;
    }
    if (messagePool.current.length === 0) {
      clearAllMessages();
      return;
    }

    const intervalMs = Math.max(1000, Math.floor(60000 / Math.max(1, config.messages.perMin)));

    const scheduleNext = () => {
      spawnSingleMessage();
      messageScheduleTimeoutRef.current = window.setTimeout(scheduleNext, intervalMs);
    };

    scheduleNext();
    return () => {
      if (messageScheduleTimeoutRef.current !== undefined) {
        window.clearTimeout(messageScheduleTimeoutRef.current);
        messageScheduleTimeoutRef.current = undefined;
      }
    };
  }, [armed, clearAllMessages, config.messages.enabled, config.messages.perMin, spawnSingleMessage]);

  useEffect(() => () => clearAllMessages(), [clearAllMessages]);

  const [spiralModeIndex, setSpiralModeIndex] = useState(0);

  useEffect(() => {
    if (!armed) return;
    const activeModes = config.backgroundModes ?? [];
    if (activeModes.length <= 1) return;
    const id = window.setInterval(() => setSpiralModeIndex((i) => i + 1), 60000);
    return () => window.clearInterval(id);
  }, [armed, config.backgroundModes]);

  const activeBgModes = config.backgroundModes ?? ["arms", "rings"];
  const currentBgMode = activeBgModes.length > 0
    ? activeBgModes[spiralModeIndex % activeBgModes.length]
    : null;

  const clickable = config.images.clickable;
  const corruption = config.images.corruption;

  return (
    <>
      {armed && (
        <>
          <style>{`
            @keyframes siteFxSpiralSpin {
              from { transform: rotate(0deg) scale(1.8); }
              to   { transform: rotate(360deg) scale(1.8); }
            }
            @keyframes siteFxCircularSpin {
              from { transform: rotate(0deg); }
              to   { transform: rotate(360deg); }
            }
            @keyframes siteFxFadeIn {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
          `}</style>

          {/* Mode: rotating conic arms */}
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 2700,
              overflow: "hidden",
              pointerEvents: "none",
              opacity: currentBgMode === "arms" ? 1 : 0,
              transition: "opacity 3s ease-in-out",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: "-50%",
                background: `conic-gradient(
                  ${hexToRgba(config.backcolour, 0.55)} 0deg,
                  transparent 45deg,
                  ${hexToRgba(config.backcolour, 0.45)} 90deg,
                  transparent 135deg,
                  ${hexToRgba(config.backcolour, 0.55)} 180deg,
                  transparent 225deg,
                  ${hexToRgba(config.backcolour, 0.45)} 270deg,
                  transparent 315deg,
                  ${hexToRgba(config.backcolour, 0.55)} 360deg
                )`,
                animation: "siteFxSpiralSpin 12s linear infinite",
                transformOrigin: "center center",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: `radial-gradient(ellipse at center, transparent 28%, ${hexToRgba(config.backcolour, 0.18)} 100%)`,
              }}
            />
          </Box>

          {/* Mode: concentric circular rings */}
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 2700,
              overflow: "hidden",
              pointerEvents: "none",
              opacity: currentBgMode === "rings" ? 1 : 0,
              transition: "opacity 3s ease-in-out",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: "-50%",
                background: `repeating-conic-gradient(
                  ${hexToRgba(config.backcolour, 0.5)} 0deg 8deg,
                  transparent 8deg 28deg,
                  ${hexToRgba(config.backcolour, 0.38)} 28deg 36deg,
                  transparent 36deg 60deg
                )`,
                animation: "siteFxCircularSpin 20s linear infinite",
                transformOrigin: "center center",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: `repeating-radial-gradient(
                  circle at center,
                  transparent 0px,
                  transparent 40px,
                  ${hexToRgba(config.backcolour, 0.12)} 40px,
                  ${hexToRgba(config.backcolour, 0.12)} 56px
                )`,
              }}
            />
          </Box>
        </>
      )}

      {armed && config.audio.enabled && activeAudio?.kind === "soundcloud" && (
        <iframe
          key={activeAudio.id}
          ref={soundCloudAudioIframeRef}
          src={activeAudio.src}
          title="site-fx-audio"
          allow="autoplay"
          style={{ position: "fixed", width: 1, height: 1, left: -9999, top: -9999, opacity: 0, pointerEvents: "none", border: 0 }}
        />
      )}

      {armed && config.audio.enabled && activeAudio?.kind === "html5" && (
        <audio
          key={activeAudio.id}
          ref={htmlAudioRef}
          src={activeAudio.src}
          autoPlay
          onEnded={() => resetAudioState()}
          onError={() => resetAudioState()}
          style={{ display: "none" }}
        />
      )}

      {(armed && config.videos.enabled || testVideoActive) && activeVideo && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 3200,
            backgroundColor: "rgba(0,0,0,0.78)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "min(1100px, 96vw)",
              maxHeight: "92vh",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
              border: "1px solid rgba(255,255,255,0.28)",
              backgroundColor: "#000",
            }}
          >
            {activeVideo.kind === "html5" ? (
              <video
                key={activeVideo.id}
                ref={html5VideoRef}
                src={activeVideo.src}
                controls
                autoPlay
                playsInline
                onEnded={() => {
                  setVideoCloseLocked(false);
                  resetVideoState();
                }}
                onError={() => {
                  setVideoCloseLocked(false);
                  resetVideoState();
                }}
                style={{ width: "100%", height: "min(84vh, 56.25vw)", maxHeight: "84vh", display: "block", background: "#000" }}
              />
            ) : (
              <iframe
                key={activeVideo.id}
                src={activeVideo.src}
                title="site-fx-video"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                style={{ width: "100%", height: "min(84vh, 56.25vw)", maxHeight: "84vh", border: 0, display: "block", background: "#000" }}
              />
            )}

            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                px: 1.2,
                py: 0.6,
                borderRadius: 999,
                bgcolor: "rgba(0,0,0,0.55)",
                border: "1px solid rgba(255,255,255,0.28)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {videoCloseLocked ? "Locked until video ends" : "Video Overlay"}
            </Box>

            <Button
              size="small"
              variant="contained"
              color={videoCloseLocked ? "inherit" : "warning"}
              disabled={videoCloseLocked}
              onClick={closeActiveVideo}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                minWidth: 0,
                px: 1.2,
                bgcolor: videoCloseLocked ? "rgba(255,255,255,0.14)" : undefined,
              }}
            >
              {videoCloseLocked ? "Locked" : "Close"}
            </Button>

            {manualPlayNeeded && activeVideo.kind === "html5" && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0,0,0,0.35)",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    const video = html5VideoRef.current;
                    if (!video) return;
                    const playPromise = video.play();
                    if (playPromise && typeof playPromise.then === "function") {
                      void playPromise.then(() => setManualPlayNeeded(false));
                    } else {
                      setManualPlayNeeded(false);
                    }
                  }}
                >
                  Tap To Play
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {(armed && config.videos.enabled || testVideoActive) && activeVideo && miniGameButtonVisible && (
        <Button
          onClick={hitMiniGameTarget}
          variant="contained"
          color="warning"
          sx={{
            position: "fixed",
            zIndex: 3300,
            left: `${miniGameTargetLeftPct}%`,
            top: `${miniGameTargetTopPct}%`,
            transform: "translate(-50%, -50%)",
            minWidth: `${Math.max(30, config.videos.miniGameTargetSizePx)}px`,
            minHeight: `${Math.max(30, config.videos.miniGameTargetSizePx)}px`,
            px: 1.5,
            py: 0.5,
            borderRadius: "999px",
            boxShadow: "0 10px 24px rgba(0,0,0,0.4)",
            textTransform: "none",
            fontWeight: 700,
            bgcolor: hexToRgba(config.backcolour, 0.88),
          }}
        >
          {miniGameButtonText} ({miniGameSecondsRemaining}s)
        </Button>
      )}

      {armed && config.images.enabled && activeImages.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            zIndex: 2800,
            overflow: "hidden",
          }}
        >
          {activeImages.map((overlayImage) => (
            <Box
              key={overlayImage.id}
              component="img"
              src={overlayImage.url}
              alt="overlay"
              onClick={() => {
                if (!clickable) return;
                removeImage(overlayImage.id);
                if (corruption) spawnImages(2);
              }}
              onError={() => removeImage(overlayImage.id)}
              sx={{
                position: "absolute",
                left: `${overlayImage.leftPct}%`,
                top: `${overlayImage.topPct}%`,
                width: `${overlayImage.widthPx}px`,
                maxWidth: "28vw",
                borderRadius: 1,
                boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
                transform: `rotate(${overlayImage.rotationDeg}deg)`,
                pointerEvents: clickable ? "auto" : "none",
                userSelect: "none",
                cursor: clickable ? "pointer" : "default",
                border: "2px solid rgba(255,255,255,0.82)",
                backgroundColor: "rgba(0,0,0,0.15)",
              }}
            />
          ))}
        </Box>
      )}

      {armed && config.messages.enabled && activeMessages.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            zIndex: 3400,
            overflow: "hidden",
          }}
        >
          {activeMessages.map((overlayMessage) => (
            <Box
              key={overlayMessage.id}
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 2,
                color: "#fff",
                fontWeight: 900,
                fontSize: `clamp(56px, ${Math.max(8, Math.min(24, Math.round(overlayMessage.sizePx / 8)))}vw, ${overlayMessage.sizePx}px)`,
                lineHeight: 1.02,
                textAlign: "center",
                letterSpacing: "0.01em",
                textTransform: "uppercase",
                maxWidth: "94vw",
                wordBreak: "break-word",
                textShadow: "0 10px 26px rgba(0,0,0,0.95)",
                backgroundColor: hexToRgba(config.backcolour, overlayMessage.backgroundOpacity),
                transformOrigin: "center",
                animation: `siteFxMessagePulse ${overlayMessage.pulseMs}ms ease-in-out infinite alternate`,
              }}
            >
              {overlayMessage.text}
            </Box>
          ))}
          <style>
            {`@keyframes siteFxMessagePulse {
              from { filter: saturate(0.9); transform: scale(0.985); }
              to { filter: saturate(1.18); transform: scale(1.015); }
            }`}
          </style>
        </Box>
      )}
    </>
  );
};

interface SiteEffectsOverlaySessionProps {
  open: boolean;
  config?: SiteEffectsConfig;
  durationSec?: number;
  ownerTest?: boolean;
  title?: string;
  testVideoTrigger?: number;
  onClose: () => void;
  onComplete?: () => void;
}

export const SiteEffectsOverlaySession: React.FC<SiteEffectsOverlaySessionProps> = ({
  open,
  config,
  durationSec,
  ownerTest = false,
  title,
  testVideoTrigger,
  onClose,
  onComplete,
}) => {
  const [sessionOpen, setSessionOpen] = useState(open);
  const [audioDiagnostics, setAudioDiagnostics] = useState<string[]>([]);

  useEffect(() => {
    setSessionOpen(open);
    if (open) {
      setAudioDiagnostics([]);
    }
  }, [open]);

  const handleAudioDiagnostic = useCallback((message: string) => {
    if (!ownerTest) return;
    setAudioDiagnostics((current) => [...current.slice(-11), message]);
  }, [ownerTest]);

  useEffect(() => {
    if (!sessionOpen || ownerTest || !durationSec || durationSec <= 0) return;
    const id = window.setTimeout(() => {
      setSessionOpen(false);
      onComplete?.();
      onClose();
    }, durationSec * 1000);
    return () => window.clearTimeout(id);
  }, [durationSec, onClose, onComplete, ownerTest, sessionOpen]);

  useEffect(() => {
    if (!sessionOpen || !ownerTest) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setSessionOpen(false);
      onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onClose, ownerTest, sessionOpen]);

  const handleOwnerClose = useCallback(() => {
    if (!ownerTest) return;
    setSessionOpen(false);
    onClose();
  }, [onClose, ownerTest]);

  if (!sessionOpen) return null;

  return (
    <>
      <MemberSiteEffectsRuntime
        armed={sessionOpen}
        config={config ?? defaultSiteEffectsConfig}
        onEmergencyStop={handleOwnerClose}
        allowEscapeDisarm={ownerTest}
        testVideoTrigger={testVideoTrigger}
        resolverBypassCache={ownerTest}
        onAudioDiagnostic={handleAudioDiagnostic}
      />
      {ownerTest && (
        <Box
          sx={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3600,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            bgcolor: "rgba(0,0,0,0.72)",
            color: "common.white",
            border: "1px solid rgba(255,255,255,0.24)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            {title ? `${title} - Press Esc to Close` : "Press Esc to Close"}
          </Typography>
          <IconButton size="small" onClick={handleOwnerClose} sx={{ color: "common.white" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {ownerTest && (
        <Paper
          elevation={8}
          sx={{
            display: "none",
            position: "fixed",
            top: 62,
            left: 16,
            zIndex: 3600,
            width: "min(640px, calc(100vw - 32px))",
            maxHeight: "38vh",
            overflow: "auto",
            p: 1.25,
            bgcolor: "rgba(0,0,0,0.75)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.22)",
          }}
        >
          <Typography variant="caption" sx={{ display: "block", mb: 0.5, opacity: 0.85 }}>
            Audio Diagnostics (owner test)
          </Typography>
          {audioDiagnostics.length === 0 ? (
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Waiting for audio diagnostics...
            </Typography>
          ) : (
            audioDiagnostics.map((line, index) => (
              <Typography key={`${index}-${line}`} variant="caption" sx={{ display: "block", fontFamily: "monospace", lineHeight: 1.35 }}>
                {line}
              </Typography>
            ))
          )}
        </Paper>
      )}
    </>
  );
};
